// src/pages/maintenance/WorkOrderDetailPage.js
// Work order detail — lifecycle actions (start/hold/resume/cancel/complete), comments, photo
// evidence, and the calibration certificate download when applicable. See docs/MAINTENANCE_MODULE.md.
import React, { useEffect, useState, useCallback } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import maintenanceService from '../../services/maintenanceService';
import userService from '../../services/userService';
import ConfirmModal from '../../components/ConfirmModal';
import MaintenanceNav from '../../components/MaintenanceNav';
import CompleteWorkOrderModal from './CompleteWorkOrderModal';
import QuickCompleteWorkOrderModal from './QuickCompleteWorkOrderModal';

const STATUS_BADGE = { open: 'secondary', in_progress: 'primary', on_hold: 'warning', completed: 'success', cancelled: 'dark' };
const PRIORITY_BADGE = { low: 'secondary', medium: 'info', high: 'warning', urgent: 'danger' };
const labelize = (s) => (s || '').replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
const fmtMoney = (n) => n != null ? `$${parseFloat(n).toFixed(2)}` : '—';
const ATTACHMENT_TYPES = ['before', 'during', 'after', 'invoice', 'other'];
const isPdf = (s3Key) => (s3Key || '').toLowerCase().endsWith('.pdf');

export default function WorkOrderDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [wo, setWo] = useState(null);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showComplete, setShowComplete] = useState(false);
  const [showQuickComplete, setShowQuickComplete] = useState(false);
  const [holdReason, setHoldReason] = useState('');
  const [showHoldPrompt, setShowHoldPrompt] = useState(false);
  const [cancelReason, setCancelReason] = useState('');
  const [showCancelPrompt, setShowCancelPrompt] = useState(false);
  const [showDeletePrompt, setShowDeletePrompt] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState('');
  const [comment, setComment] = useState('');
  const [uploading, setUploading] = useState(false);
  const [uploadType, setUploadType] = useState('other');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const data = await maintenanceService.getWorkOrder(id);
      setWo(data);
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { userService.listUsers().then(setUsers).catch(() => setUsers([])); }, []);

  const completedByNames = (wo?.completedByUserIds || [])
    .map(uid => users.find(u => u.id === uid))
    .filter(Boolean)
    .map(u => u.username || `${u.firstName || ''} ${u.lastName || ''}`.trim())
    .join(', ');

  const doAction = async (fn) => { await fn(); load(); };

  const submitComment = async () => {
    if (!comment.trim()) return;
    await maintenanceService.addWoComment(id, comment);
    setComment('');
    load();
  };

  const uploadFiles = async (fileList) => {
    const files = Array.from(fileList);
    if (!files.length) return;
    setUploading(true);
    try {
      // Sequential, not parallel — S3 presigned URLs and the save-record call are per-file, and
      // keeping it simple/predictable matters more than upload speed for a handful of attachments.
      for (const file of files) {
        const { uploadUrl, key } = await maintenanceService.getWoPhotoUploadUrl(id, file.type);
        await maintenanceService.uploadToPresignedUrl(uploadUrl, file);
        await maintenanceService.addWoPhoto(id, { s3Key: key, photoType: uploadType, caption: file.name });
      }
      load();
    } finally {
      setUploading(false);
    }
  };

  const downloadCert = async () => {
    const { url } = await maintenanceService.getCalibrationCert(id);
    window.open(url, '_blank');
  };

  const handleDelete = async () => {
    setDeleting(true);
    setDeleteError('');
    try {
      await maintenanceService.deleteWorkOrder(id);
      navigate('/maintenance/work-orders');
    } catch (err) {
      setDeleteError(err?.response?.data?.message || 'Failed to delete work order.');
      setDeleting(false);
      setShowDeletePrompt(false);
    }
  };

  if (loading) return <div className="container-fluid py-3 text-center text-muted">Loading…</div>;
  if (!wo) return <div className="container-fluid py-3 text-center text-muted">Work order not found.</div>;

  return (
    <>
      <MaintenanceNav />
      <div className="container-fluid py-3">
      <Link to="/maintenance/work-orders" className="text-muted" style={{ fontSize: 13 }}>&larr; All Work Orders</Link>
      <div className="d-flex justify-content-between align-items-start mt-1 mb-3">
        <div>
          <h4 className="mb-0">{wo.woNumber} — {wo.title}</h4>
          <div className="d-flex gap-2 mt-1">
            <span className={`badge bg-${STATUS_BADGE[wo.status] || 'secondary'}`}>{labelize(wo.status)}</span>
            <span className={`badge bg-${PRIORITY_BADGE[wo.priority] || 'secondary'}`}>{labelize(wo.priority)}</span>
            <span className="badge bg-light text-dark">{labelize(wo.type)}</span>
          </div>
        </div>
        <div className="d-flex gap-2">
          {wo.status === 'open' && <button className="btn btn-sm btn-primary" onClick={() => doAction(() => maintenanceService.startWorkOrder(id))}>Start</button>}
          {wo.status === 'in_progress' && <button className="btn btn-sm btn-outline-warning" onClick={() => setShowHoldPrompt(true)}>Hold</button>}
          {wo.status === 'on_hold' && <button className="btn btn-sm btn-outline-primary" onClick={() => doAction(() => maintenanceService.resumeWorkOrder(id))}>Resume</button>}
          {['open', 'in_progress', 'on_hold'].includes(wo.status) && (
            <>
              {wo.type === 'preventive' && !wo.isCalibrationWo && (
                <button className="btn btn-sm btn-outline-success" onClick={() => setShowQuickComplete(true)}>⚡ Quick Complete</button>
              )}
              <button className="btn btn-sm btn-success" onClick={() => setShowComplete(true)}>Complete</button>
              <button className="btn btn-sm btn-outline-danger" onClick={() => setShowCancelPrompt(true)}>Cancel</button>
            </>
          )}
          {wo.status !== 'completed' && (
            <button className="btn btn-sm btn-outline-danger" onClick={() => setShowDeletePrompt(true)}>Delete</button>
          )}
          {wo.calibrationCertKey && <button className="btn btn-sm btn-outline-secondary" onClick={downloadCert}>Download Cert</button>}
        </div>
      </div>

      {deleteError && <div className="alert alert-danger py-2" style={{ fontSize: 13 }}>{deleteError}</div>}

      <div className="row g-3">
        <div className="col-md-4">
          <div className="card">
            <div className="card-body">
              <h6 className="card-title">Details</h6>
              <table className="table table-sm table-borderless mb-0" style={{ fontSize: 13 }}>
                <tbody>
                  <tr><td className="text-muted">Asset</td><td>{wo.Asset ? <Link to={`/maintenance/assets/${wo.Asset.id}`}>{wo.Asset.name}</Link> : '—'}</td></tr>
                  <tr><td className="text-muted">Location</td><td>{wo.Location?.name || '—'}</td></tr>
                  <tr><td className="text-muted">Vendor</td><td>{wo.Vendor?.name || '—'}</td></tr>
                  <tr><td className="text-muted">Technician</td><td>{wo.vendorTechnicianName || '—'}</td></tr>
                  <tr><td className="text-muted">PM Template</td><td>{wo.PmTemplate?.title || '—'}</td></tr>
                  <tr><td className="text-muted">Due</td><td>{wo.dueAt ? new Date(wo.dueAt).toLocaleDateString() : '—'}</td></tr>
                  <tr><td className="text-muted">Started</td><td>{wo.startedAt ? new Date(wo.startedAt).toLocaleString() : '—'}</td></tr>
                  <tr><td className="text-muted">Completed</td><td>{wo.completedAt ? new Date(wo.completedAt).toLocaleString() : '—'}</td></tr>
                </tbody>
              </table>
              {wo.description && <p className="text-muted mt-2" style={{ fontSize: 13 }}>{wo.description}</p>}
            </div>
          </div>

          {wo.status === 'completed' && (
            <div className="card mt-3">
              <div className="card-body">
                <h6 className="card-title">Cost Summary</h6>
                <table className="table table-sm table-borderless mb-0" style={{ fontSize: 13 }}>
                  <tbody>
                    <tr><td className="text-muted">Actual Hours</td><td>{wo.actualHours ?? '—'}</td></tr>
                    <tr><td className="text-muted">Labor Cost</td><td>{fmtMoney(wo.laborCost)}</td></tr>
                    <tr><td className="text-muted">Parts Cost</td><td>{fmtMoney(wo.partsCost)}</td></tr>
                    <tr><td className="text-muted fw-semibold">Total Cost</td><td className="fw-semibold">{fmtMoney(wo.totalCost)}</td></tr>
                    {completedByNames && <tr><td className="text-muted">Completed By</td><td>{completedByNames}</td></tr>}
                    {wo.rootCause && <tr><td className="text-muted">Root Cause</td><td>{wo.rootCause}</td></tr>}
                    {wo.correctionNotes && <tr><td className="text-muted">Corrective Action</td><td>{wo.correctionNotes}</td></tr>}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>

        <div className="col-md-8">
          <div className="card mb-3">
            <div className="card-body">
              <h6 className="card-title">Photos &amp; Documents</h6>
              <div className="d-flex flex-wrap gap-2 mb-3">
                {(wo.photos || []).map(p => (
                  <a key={p.id} href={p.signedUrl} target="_blank" rel="noreferrer" className="text-decoration-none" title={p.caption || ''}>
                    {isPdf(p.s3Key) ? (
                      <div style={{ width: 96, height: 96, borderRadius: 4, border: '1px solid #dee2e6', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', background: '#f8f9fa' }}>
                        <span style={{ fontSize: 28 }}>📄</span>
                        <span className="text-muted text-truncate" style={{ fontSize: 10, maxWidth: 84 }}>{p.caption || 'PDF'}</span>
                      </div>
                    ) : (
                      <img src={p.signedUrl} alt={p.caption || 'WO attachment'} style={{ width: 96, height: 96, objectFit: 'cover', borderRadius: 4 }} />
                    )}
                    {p.photoType && p.photoType !== 'other' && (
                      <div className="text-center"><span className="badge bg-secondary-subtle text-secondary mt-1" style={{ fontSize: 9 }}>{labelize(p.photoType)}</span></div>
                    )}
                  </a>
                ))}
              </div>
              <div className="d-flex gap-2 align-items-center flex-wrap">
                <select className="form-select form-select-sm" style={{ width: 'auto' }} value={uploadType} onChange={e => setUploadType(e.target.value)} disabled={uploading}>
                  {ATTACHMENT_TYPES.map(t => <option key={t} value={t}>{labelize(t)}</option>)}
                </select>
                <input
                  type="file"
                  accept="image/jpeg,image/png,application/pdf"
                  multiple
                  disabled={uploading}
                  onChange={e => { if (e.target.files.length) uploadFiles(e.target.files); e.target.value = ''; }}
                />
                {uploading && <span className="text-muted" style={{ fontSize: 12 }}>Uploading…</span>}
              </div>
              <div className="form-text">JPEG, PNG, or PDF — pick a type above (e.g. "Invoice") before selecting the file(s).</div>
            </div>
          </div>

          <div className="card">
            <div className="card-body">
              <h6 className="card-title">Comments</h6>
              <div style={{ maxHeight: 260, overflowY: 'auto' }}>
                {(wo.Comments || []).map(c => (
                  <div key={c.id} className="mb-2 pb-2 border-bottom">
                    <div style={{ fontSize: 12, fontWeight: 600 }}>{c.User?.username || 'User'}</div>
                    <div style={{ fontSize: 13 }}>{c.comment}</div>
                  </div>
                ))}
                {(!wo.Comments || wo.Comments.length === 0) && <p className="text-muted" style={{ fontSize: 13 }}>No comments yet.</p>}
              </div>
              <div className="input-group input-group-sm mt-2">
                <input className="form-control" placeholder="Add a comment…" value={comment} onChange={e => setComment(e.target.value)} onKeyDown={e => e.key === 'Enter' && submitComment()} />
                <button className="btn btn-outline-primary" onClick={submitComment}>Post</button>
              </div>
            </div>
          </div>
        </div>
      </div>

      {showComplete && (
        <CompleteWorkOrderModal workOrder={wo} onSave={() => { setShowComplete(false); load(); }} onClose={() => setShowComplete(false)} />
      )}

      {showQuickComplete && (
        <QuickCompleteWorkOrderModal workOrder={wo} onSave={() => { setShowQuickComplete(false); load(); }} onClose={() => setShowQuickComplete(false)} />
      )}

      <ConfirmModal
        show={showHoldPrompt}
        title="Hold Work Order"
        message={
          <input className="form-control form-control-sm" placeholder="Reason for hold" value={holdReason} onChange={e => setHoldReason(e.target.value)} />
        }
        confirmLabel="Hold"
        variant="warning"
        onConfirm={() => { doAction(() => maintenanceService.holdWorkOrder(id, holdReason)); setShowHoldPrompt(false); setHoldReason(''); }}
        onCancel={() => setShowHoldPrompt(false)}
      />

      <ConfirmModal
        show={showCancelPrompt}
        title="Cancel Work Order"
        message={
          <input className="form-control form-control-sm" placeholder="Reason for cancellation" value={cancelReason} onChange={e => setCancelReason(e.target.value)} />
        }
        confirmLabel="Cancel Work Order"
        onConfirm={() => { doAction(() => maintenanceService.cancelWorkOrder(id, cancelReason)); setShowCancelPrompt(false); setCancelReason(''); }}
        onCancel={() => setShowCancelPrompt(false)}
      />

      <ConfirmModal
        show={showDeletePrompt}
        title="Delete Work Order"
        message={`Permanently delete ${wo.woNumber} — ${wo.title}? This removes it completely (not just cancels it) along with its comments and photos. This can't be undone.`}
        confirmLabel={deleting ? 'Deleting…' : 'Delete Permanently'}
        onConfirm={handleDelete}
        onCancel={() => setShowDeletePrompt(false)}
      />
      </div>
    </>
  );
}
